import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal } from 'reactstrap';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import { getFirebaseBackend } from 'helpers/firebaseHelper';

import { PostContentModalLayout, DatePickerPortal } from './styles';

const PostContentModal = ({ isOpen, toggle, accountId, guidelineId, onConfirm }) => {
  const { t: translate } = useTranslation();
  const firebaseHelper = getFirebaseBackend();
  const [publicationDate, setPublicationDate] = useState(new Date());
  const [publicationLink, setPublicationLink] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [datePickerPosition, setDatePickerPosition] = useState(null);
  const datePickerTriggerRef = useRef(null);

  useLayoutEffect(() => {
    if (isDatePickerOpen && datePickerTriggerRef.current) {
      const rect = datePickerTriggerRef.current.getBoundingClientRect();
      setDatePickerPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, [isDatePickerOpen]);

  useEffect(() => {
    if (!isDatePickerOpen) return undefined;

    const handleClickOutside = (event) => {
      if (datePickerTriggerRef.current?.contains(event.target)) return;
      if (event.target.closest('.react-datepicker')) return;
      setIsDatePickerOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen]);

  const handleConfirm = async () => {
    try {
      setIsSending(true);
      const isProduction = process.env.REACT_APP_BUILD_TYPE === 'production';
      const firebaseToken = await firebaseHelper.getIdToken();

      await axios.post(
        `${process.env.REACT_APP_API_URL}/updateGuidelineStatus`,
        {
          accountId,
          guidelineId,
          publishedUrl: publicationLink,
          publishedDate: publicationDate.getTime(),
          development: !isProduction,
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );

      setIsSending(false);
      onConfirm({ publicationDate, publicationLink });
    } catch (error) {
      setIsSending(false);
      console.log(error);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered contentClassName="border-0 bg-transparent">
      <PostContentModalLayout>
        <div className="modal-header-row">
          <span>{translate('Mark content as published')}</span>
          <i className="bx bx-x" onClick={toggle} />
        </div>
        <div className="modal-body-content">
          <div className="form-control-group">
            <label>{translate('Publication date')}</label>
            <div
              ref={datePickerTriggerRef}
              className="date-picker-trigger"
              onClick={() => setIsDatePickerOpen((prev) => !prev)}
            >
              <div className="date-picker-trigger-value">
                <i className="bx bx-calendar" />
                <span>{publicationDate.toLocaleDateString('pt-BR')}</span>
              </div>
              <i className="bx bx-chevron-down" />
            </div>
            {isDatePickerOpen &&
              datePickerPosition &&
              createPortal(
                <DatePickerPortal
                  style={{ top: datePickerPosition.top, left: datePickerPosition.left }}
                >
                  <DatePicker
                    selected={publicationDate}
                    inline
                    onChange={(date) => {
                      setPublicationDate(date);
                      setIsDatePickerOpen(false);
                    }}
                  />
                </DatePickerPortal>,
                document.body,
              )}
          </div>
          <div className="form-control-group">
            <label>{translate('Publication link')}</label>
            <input
              type="text"
              className="link-input"
              placeholder={translate('Enter the link to the post')}
              value={publicationLink}
              onChange={(e) => setPublicationLink(e.target.value)}
            />
          </div>
        </div>
        <div className="modal-footer-row">
          <button type="button" className="btn-cancel" onClick={toggle} disabled={isSending}>
            {translate('Cancel')}
          </button>
          <button
            type="button"
            className="btn-publish"
            onClick={handleConfirm}
            disabled={isSending}
          >
            {translate('Mark as published')}
          </button>
        </div>
      </PostContentModalLayout>
    </Modal>
  );
};

PostContentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  accountId: PropTypes.string,
  guidelineId: PropTypes.string,
  onConfirm: PropTypes.func.isRequired,
};

export default PostContentModal;
